import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Form, Input, InputNumber, Switch } from "antd";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import ImageUploadField from "../../components/global/imageUpload/ImageUploadField.jsx";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import ConfirmationModal from "../../components/global/modal/ConfirmationModal.jsx";
import MyCKEditor from "../../components/global/ckEditor/CKEditor.jsx";
import getEducationUnitsColumns from "../../columns/educationUnits.js";
import {
  GetEducationUnitsApi,
  AddUpdateEducationUnitApi,
  DeleteEducationUnitApi,
} from "../../api/request/education.js";
import toast from "react-hot-toast";

const CKEditorFormField = ({ value, onChange }) => (
  <MyCKEditor editorData={value || ""} setEditorData={onChange} />
);

const getImageDisplayUrl = (fileUrl) => {
  if (!fileUrl) return fileUrl;
  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) return fileUrl;
  const parts = String(fileUrl).replace(/\\/g, "/").split("/");
  const filename = parts[parts.length - 1];
  const base = process.env.REACT_APP_BASE_URL || "";
  const origin = base.replace(/\/api\/?$/, "");
  return `${origin}/UploadFiles/EducationUnit/${filename}`;
};

export default function EducationUnits() {
  const { sectionId } = useParams();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const getEducationUnits = async () => {
    if (!sectionId) return;
    try {
      setIsLoading(true);
      const response = await GetEducationUnitsApi(sectionId);
      const responseData = response?.data;
      if (responseData?.success) {
        const mappedData = (responseData?.data || []).map((item) => ({ ...item, key: item?.id }));
        setData(mappedData);
      }
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) return;
    try {
      setIsDeleteLoading(true);
      const response = await DeleteEducationUnitApi(itemToDelete.id);
      if (response?.data?.success) {
        toast.success(response?.data?.message || "Education unit deleted successfully.");
        setDeleteModalOpen(false);
        setItemToDelete(null);
        getEducationUnits();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedItem(null);
    form.setFieldsValue({ title: "", description: "", unitOrder: 0, isActive: true, file: undefined });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    form.setFieldsValue({
      title: item?.title,
      description: item?.description || "",
      unitOrder: item?.unitOrder ?? 0,
      isActive: item?.isActive ?? true,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitLoading(true);
      const apiHeader = { headers: { Accept: "application/json", "Content-Type": "multipart/form-data" } };
      const formData = new FormData();
      formData.append("SectionId", sectionId);
      formData.append("Title", values?.title);
      formData.append("Description", values?.description || "");
      values?.unitOrder && formData.append("UnitOrder", Number(values?.unitOrder) ?? 0);
      formData.append("IsActive", values?.isActive ?? true);
      if (selectedItem?.id) {
        formData.append("Id", selectedItem.id);
      }
      const fileToUpload = values?.file?.[0]?.originFileObj;
      if (fileToUpload) {
        formData.append("File", fileToUpload);
      } else if (selectedItem?.id && selectedItem?.fileUrl) {
        formData.append("FileUrl", selectedItem.fileUrl);
      }
      const response = await AddUpdateEducationUnitApi(formData, apiHeader);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
          (selectedItem ? "Education unit updated successfully." : "Education unit added successfully.")
        );
        handleCloseModal();
        getEducationUnits();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const columns = useMemo(
    () => getEducationUnitsColumns(openEditModal, openDeleteModal),
    []
  );

  useEffect(() => {
    getEducationUnits();
  }, [sectionId]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedItem(null);
      form.resetFields();
    }
  }, [isModalOpen]);

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList || [];
  };

  return (
    <div>
      <div className="flex justify-end items-center flex-wrap gap-5 mb-5">
        <button
          onClick={openAddModal}
          className="flex items-center bg-primary text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover duration-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              fillRule="evenodd"
              d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
              clipRule="evenodd"
            />
          </svg>
          Add Unit
        </button>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            page={1}
            pageSize={10}
          />
        </div>
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={handleCloseModal}
        title={selectedItem ? "Edit Education Unit" : "Add Education Unit"}
        width={800}
      >
        <Form
          className="w-full"
          autoComplete="off"
          form={form}
          initialValues={{ title: "", description: "", unitOrder: 0, isActive: true }}
          onFinish={handleSubmit}
        >
          <div className="grid sm:grid-cols-1 gap-5">
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Title</label>
              <Form.Item
                name="title"
                rules={[
                  { required: true, message: "Please enter a title." },
                  { whitespace: true, message: "Title cannot be empty." },
                  { max: 255, message: "Title cannot exceed 255 characters." },
                ]}
              >
                <Input placeholder="Title" className="mt-1 w-full py-2" maxLength={255} />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Description</label>
              <Form.Item
                name="description"
                rules={[{ required: true, message: "Please enter a description." }]}
              >
                <CKEditorFormField />
              </Form.Item>
            </div>
            {selectedItem && (
              <div className="input-white">
                <label className="text-description text-[#2F2B3DCC] font-medium">Unit Order</label>
                <Form.Item
                  name="unitOrder"
                  rules={[
                    { required: true, message: "Please enter unit order." },
                    { type: "number", min: 0, message: "Unit order must be a positive number." },
                    {
                      validator: (_, value) => {
                        if (value != null && value !== "" && !Number.isInteger(Number(value))) {
                          return Promise.reject(new Error("Unit order must be a whole number."));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Unit Order"
                    className="mt-1 w-full py-2"
                    min={0}
                    precision={0}
                    controls
                    style={{ width: "100%" }}
                    parser={(value) => {
                      const v = String(value ?? "").replace(/[^\d]/g, "");
                      return v === "" ? "" : parseInt(v, 10);
                    }}
                    onKeyDown={(e) => {
                      const allowed = ["Backspace", "Tab", "Delete", "ArrowLeft", "ArrowRight", "Home", "End"];
                      if (allowed.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                  />
                </Form.Item>
              </div>
            )}
            <ImageUploadField
              form={form}
              selectedItem={selectedItem}
              normFile={normFile}
              getDisplayUrl={getImageDisplayUrl}
            />
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Status</label>
              <Form.Item name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
              </Form.Item>
            </div>
          </div>
          <div className="flex justify-end mt-8">
            <LoadableButton
              className="bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500"
              disabled={isSubmitLoading}
              type="submit"
              lable={selectedItem ? "Update" : "Submit"}
              isLoading={isSubmitLoading}
              loadingLable={selectedItem ? "Updating..." : "Adding..."}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        setIsOpen={(open) => {
          setDeleteModalOpen(open);
          if (!open) setItemToDelete(null);
        }}
        message="Are you sure you want to delete this education unit?"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
};
